import { z } from 'zod';
import {
    CloverTerminalTokenizationOutcome,
    type CloverCardEntryMethod,
    type CloverTerminalTokenizationErrorBody,
    type CloverTerminalTokenizationRequestBody,
    type CloverTerminalTokenizationRequestDetails,
    type CloverTerminalTokenizationRequestHeaders,
    type CloverTerminalTokenizationResponseDetails,
    type CloverTerminalTokenizationSuccessBody,
} from '../../../types/ipg/clover/clover-terminal-tokenization.types';

export const CloverCardEntryMethodSchema: z.ZodType<CloverCardEntryMethod> = z.enum([
    'MAG_STRIPE',
    'EMV',
    'NFC',
    'MANUAL',
    'DEFAULT',
]);

export const CloverTerminalTokenizationRequestHeadersSchema: z.ZodObject<{
    deviceId: z.ZodString;
    idempotencyKey: z.ZodOptional<z.ZodString>;
    posId: z.ZodString;
    userAgent: z.ZodString;
    timeoutSeconds: z.ZodOptional<z.ZodNumber>;
}> = z.object({
    deviceId: z.string().min(1),
    idempotencyKey: z.string().min(1).optional(),
    posId: z.string().min(1),
    userAgent: z.string().min(1),
    timeoutSeconds: z.number().int().min(0).max(300).optional(),
}) satisfies z.ZodType<CloverTerminalTokenizationRequestHeaders>;

export const CloverTerminalTokenizationRequestBodySchema: z.ZodObject<{
    cardEntryMethods: z.ZodOptional<z.ZodArray<typeof CloverCardEntryMethodSchema>>;
    tokenType: z.ZodLiteral<'ECOMM_COMPAT'>;
}> = z.object({
    cardEntryMethods: z.array(CloverCardEntryMethodSchema).optional(),
    tokenType: z.literal('ECOMM_COMPAT'),
}) satisfies z.ZodType<CloverTerminalTokenizationRequestBody>;

export const CloverTerminalTokenizationRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'POST'>;
    path: z.ZodLiteral<'/v1/card/tokens'>;
    headers: typeof CloverTerminalTokenizationRequestHeadersSchema;
    body: typeof CloverTerminalTokenizationRequestBodySchema;
}> = z.object({
    method: z.literal('POST'),
    path: z.literal('/v1/card/tokens'),
    headers: CloverTerminalTokenizationRequestHeadersSchema,
    body: CloverTerminalTokenizationRequestBodySchema,
}) satisfies z.ZodType<CloverTerminalTokenizationRequestDetails>;

export const CloverTerminalTokenizationSuccessBodySchema: z.ZodObject<{
    expirationDate: z.ZodOptional<z.ZodString>;
    first6: z.ZodOptional<z.ZodString>;
    last4: z.ZodOptional<z.ZodString>;
    name: z.ZodOptional<z.ZodString>;
    token: z.ZodString;
    tokenType: z.ZodLiteral<'ECOMM_COMPAT'>;
}> = z.object({
    expirationDate: z
        .string()
        .regex(/^\d{4}$/)
        .optional(),
    first6: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
    last4: z
        .string()
        .regex(/^\d{4}$/)
        .optional(),
    name: z.string().min(1).optional(),
    token: z.string().min(5).max(72).startsWith('clv_'),
    tokenType: z.literal('ECOMM_COMPAT'),
}) satisfies z.ZodType<CloverTerminalTokenizationSuccessBody>;

export const CloverTerminalTokenizationErrorBodySchema: z.ZodObject<{
    message: z.ZodString;
    type: z.ZodString;
    code: z.ZodOptional<z.ZodString>;
    requestId: z.ZodOptional<z.ZodString>;
    requestType: z.ZodOptional<z.ZodString>;
}> = z.object({
    message: z.string().min(1),
    type: z.string().min(1),
    code: z.string().min(1).optional(),
    requestId: z.string().min(1).optional(),
    requestType: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverTerminalTokenizationErrorBody>;

export const CloverTerminalTokenizationCreatedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverTerminalTokenizationOutcome.TOKEN_CREATED>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CloverTerminalTokenizationSuccessBodySchema;
}> = z.object({
    outcome: z.literal(CloverTerminalTokenizationOutcome.TOKEN_CREATED),
    httpStatus: z.literal(200),
    body: CloverTerminalTokenizationSuccessBodySchema,
});

export const CloverTerminalTokenizationCanceledResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverTerminalTokenizationOutcome.CANCELED>;
    httpStatus: z.ZodLiteral<209>;
    body: typeof CloverTerminalTokenizationErrorBodySchema;
}> = z.object({
    outcome: z.literal(CloverTerminalTokenizationOutcome.CANCELED),
    httpStatus: z.literal(209),
    body: CloverTerminalTokenizationErrorBodySchema,
});

export const CloverTerminalTokenizationUnsupportedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverTerminalTokenizationOutcome.UNSUPPORTED_TERMINAL>;
    httpStatus: z.ZodLiteral<501>;
    body: typeof CloverTerminalTokenizationErrorBodySchema;
}> = z.object({
    outcome: z.literal(CloverTerminalTokenizationOutcome.UNSUPPORTED_TERMINAL),
    httpStatus: z.literal(501),
    body: CloverTerminalTokenizationErrorBodySchema,
});

export const CloverTerminalTokenizationMalformedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverTerminalTokenizationOutcome.MALFORMED_RESPONSE>;
    httpStatus: z.ZodNumber;
    body: z.ZodUnknown;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CloverTerminalTokenizationOutcome.MALFORMED_RESPONSE),
    httpStatus: z.number().int().positive(),
    body: z.unknown(),
    message: z.string().min(1),
});

export const CloverTerminalTokenizationResponseDetailsSchema: z.ZodType<CloverTerminalTokenizationResponseDetails> =
    z.discriminatedUnion('outcome', [
        CloverTerminalTokenizationCreatedResponseDetailsSchema,
        CloverTerminalTokenizationCanceledResponseDetailsSchema,
        CloverTerminalTokenizationUnsupportedResponseDetailsSchema,
        CloverTerminalTokenizationMalformedResponseDetailsSchema,
    ]);
